import { useMemo } from 'react'
import { useMeSessionQuery } from '@/features/auth/hooks/use-me-session-query'

type PermissionInput = string | readonly string[]

function normalizePermissions(perm: PermissionInput): string[] {
  if (typeof perm === 'string') return [perm]
  return [...perm]
}

export function hasAdministratorRole(roles: readonly string[] | undefined): boolean {
  if (!roles?.length) return false
  return roles.some((role) => {
    const lower = role.toLowerCase()
    return lower === 'administrator' || lower === 'systemadministrator'
  })
}

type UseHasPermissionOptions = {
  /** Matches backend payroll policies: Administrator role bypasses capability checks. */
  administratorBypass?: boolean
}

/** True when the active session includes the given capability string(s). */
export function useHasPermission(
  perm: PermissionInput,
  mode: 'all' | 'any' = 'all',
  options: UseHasPermissionOptions = {},
): boolean {
  const session = useMeSessionQuery()
  const capabilities = session.data?.capabilities
  const roles = session.data?.roles
  return useMemo(() => {
    if (options.administratorBypass && hasAdministratorRole(roles)) {
      return true
    }
    const required = normalizePermissions(perm)
    if (required.length === 0) return true
    const caps = new Set(capabilities ?? [])
    if (mode === 'any') {
      return required.some((p) => caps.has(p))
    }
    return required.every((p) => caps.has(p))
  }, [mode, perm, capabilities, roles, options.administratorBypass])
}

export function useHasAnyPermission(perms: readonly string[]): boolean {
  return useHasPermission(perms, 'any')
}

export function useIsAdministrator(): boolean {
  const session = useMeSessionQuery()
  return useMemo(() => hasAdministratorRole(session.data?.roles), [session.data?.roles])
}

export function useHasAllPermissions(perms: readonly string[]): boolean {
  return useHasPermission(perms, 'all')
}
